import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { Mail, Phone, User } from "lucide-react";

// Within a section: Manager → Chair → Vice Chair → Treasurer → Secretary → rest.
function rank(member) {
  const t = (member.title || "").toLowerCase();
  if (t.includes("manager")) return 0;
  if (t.includes("vice")) return 2; // catch "vice chair" before "chair"
  if (t.includes("chair")) return 1;
  if (t.includes("treasurer")) return 3;
  if (t.includes("secretary")) return 4;
  return 5;
}

function sortByRank(list) {
  return [...list].sort((a, b) => {
    const ra = rank(a);
    const rb = rank(b);
    if (ra !== rb) return ra - rb;
    const so = (a.sort_order ?? 0) - (b.sort_order ?? 0);
    if (so !== 0) return so;
    return (a.name || "").localeCompare(b.name || "");
  });
}

export default function Leadership() {
  const [team, setTeam] = useState([]);
  useEffect(() => { api.get("/team").then((r) => setTeam(r.data)); }, []);

  const staff = useMemo(() => sortByRank(team.filter((m) => m.role !== "board")), [team]);
  const board = useMemo(() => sortByRank(team.filter((m) => m.role === "board")), [team]);

  return (
    <div data-testid="leadership-page" className="max-w-7xl mx-auto px-6 lg:px-10 py-24 anim-fadeup">
      <div className="section-label">Leadership</div>
      <h1 className="font-display font-bold text-4xl sm:text-5xl text-slate-950 mt-3">Board & Staff.</h1>
      <p className="mt-3 text-slate-600 max-w-2xl">
        The people who keep KSME running — from the Airport Manager and staff on the ramp to the volunteer board members
        shaping long-term strategy.
      </p>

      <Section title="Airport Board" people={board} />

      <div className="my-16 flex items-center gap-6" aria-hidden="true">
        <div className="flex-1 h-px bg-slate-200" />
        <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-slate-400">Airport Staff</div>
        <div className="flex-1 h-px bg-slate-200" />
      </div>

      <Section title="Airport Staff" people={staff} hideHeader />
    </div>
  );
}

function Section({ title, people, hideHeader }) {
  if (!people || people.length === 0) return null;
  return (
    <section>
      {!hideHeader && <div className="text-xs font-mono uppercase tracking-widest text-sky-600 mb-6 mt-16">{title}</div>}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {people.map((p) => (
          <article key={p.id} data-testid={`team-${p.id}`} className="bg-white border border-slate-200 p-6">
            <div className="flex items-start gap-4">
              <div className="h-16 w-16 bg-slate-100 border border-slate-200 flex items-center justify-center overflow-hidden shrink-0">
                {p.photo_url ? (
                  <img src={p.photo_url} alt={p.name} className="h-full w-full object-cover" />
                ) : (
                  <User className="h-7 w-7 text-slate-400" />
                )}
              </div>
              <div className="min-w-0">
                <div className="font-display text-xl font-semibold text-slate-950">{p.name}</div>
                <div className="text-xs font-mono uppercase tracking-widest text-sky-600 mt-1">{p.title}</div>
              </div>
            </div>
            {p.bio && <p className="mt-4 text-sm text-slate-600 leading-relaxed">{p.bio}</p>}
            <dl className="mt-4 space-y-1 text-sm">
              {p.email && (
                <a href={`mailto:${p.email}`} className="flex items-center gap-2 text-slate-700 hover:text-sky-600">
                  <Mail className="h-4 w-4" /> {p.email}
                </a>
              )}
              {p.phone && (
                <a href={`tel:${p.phone}`} className="flex items-center gap-2 text-slate-700 hover:text-sky-600">
                  <Phone className="h-4 w-4" /> {p.phone}
                </a>
              )}
            </dl>
          </article>
        ))}
      </div>
    </section>
  );
}
